import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Operator } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Input, Label } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';

export function AccountPage() {
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<{ operator: Operator }>('/api/auth/me'),
  });

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Account</h1>
        <p className="text-sm text-slate-500 mt-1">Your operator profile and login methods.</p>
      </div>

      {me.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : me.data ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
            </CardHeader>
            <CardBody className="text-sm space-y-2">
              <Row label="Email" value={me.data.operator.email} />
              <Row label="Name" value={me.data.operator.name ?? '—'} />
              <Row
                label="Last login"
                value={me.data.operator.last_login ? formatDate(me.data.operator.last_login) : '—'}
              />
            </CardBody>
          </Card>

          <PasswordCard operator={me.data.operator} />
        </>
      ) : null}
    </div>
  );
}

function PasswordCard({ operator }: { operator: Operator }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.post('/api/operators/me/password', {
        current_password: operator.has_password ? form.current_password : undefined,
        new_password: form.new_password,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      setForm({ current_password: '', new_password: '', confirm: '' });
      setError(null);
      setOk('Password updated.');
    },
    onError: (err: Error) => setError(err.message),
  });

  const clear = useMutation({
    mutationFn: () => api.del('/api/operators/me/password'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      setOk('Password removed. Magic-link only.');
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Password</CardTitle>
          {operator.has_password ? (
            <Badge tone="success">set</Badge>
          ) : (
            <Badge tone="neutral">magic-link only</Badge>
          )}
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-xs text-slate-500">
          Optional second login path. Magic link will always work whether or not a password is set.
        </p>
        {ok && <div className="text-xs text-emerald-700 bg-emerald-50 rounded-md px-3 py-2">{ok}</div>}
        {error && (
          <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>
        )}
        {operator.has_password && (
          <div>
            <Label htmlFor="current">Current password</Label>
            <Input
              id="current"
              type="password"
              autoComplete="current-password"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            />
          </div>
        )}
        <div>
          <Label htmlFor="new">New password</Label>
          <Input
            id="new"
            type="password"
            autoComplete="new-password"
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            placeholder="At least 8 characters"
          />
        </div>
        <div>
          <Label htmlFor="confirm">Confirm new password</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => {
              setOk(null);
              if (!form.new_password || form.new_password.length < 8) {
                setError('Password must be at least 8 characters.');
                return;
              }
              if (form.new_password !== form.confirm) {
                setError('Passwords do not match.');
                return;
              }
              if (operator.has_password && !form.current_password) {
                setError('Enter your current password.');
                return;
              }
              setError(null);
              save.mutate();
            }}
            disabled={save.isPending}
          >
            {operator.has_password ? 'Change password' : 'Set password'}
          </Button>
          {operator.has_password && (
            <Button
              variant="outline"
              onClick={() => {
                if (confirm('Remove password? You can still sign in with magic links.')) {
                  setOk(null);
                  clear.mutate();
                }
              }}
              disabled={clear.isPending}
            >
              Remove password
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500 text-xs uppercase tracking-wide">{label}</span>
      <span className="text-slate-900 font-mono text-xs text-right break-all">{value}</span>
    </div>
  );
}
